import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, Loader2, AlertCircle, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const PropertyLoginModal = ({ open, onClose, onSuccess, onRegisterClick }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email: formData.email,
        password: formData.password
      });

      const { token, user } = response.data;

      // Check if user is property owner
      if (user.role !== 'property_owner') {
        setError('Questo account non è registrato come proprietario di struttura');
        setLoading(false);
        return;
      }

      // Save token
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      toast.success(' Login effettuato con successo!');
      onClose();
      if (onSuccess) onSuccess(user);
    } catch (err) {
      setError('Email o password errati');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-orange-600" />
            Login Struttura
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Accedi per gestire la tua struttura e le richieste
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Email */}
          <div>
            <Label htmlFor="property-email" className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-orange-600" />
              Email
            </Label>
            <Input
              id="property-email"
              type="email"
              placeholder="hotel@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full"
            />
          </div>

          {/* Password */}
          <div>
            <Label htmlFor="property-password" className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-orange-600" />
              Password
            </Label>
            <Input
              id="property-password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              className="w-full"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 shadow-lg shadow-orange-500/30 h-12"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Accesso in corso...
              </>
            ) : (
              'Accedi'
            )}
          </Button>

          {/* Register Link */}
          <div className="text-center pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              Non hai ancora registrato la tua struttura?{' '}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  if (onRegisterClick) onRegisterClick();
                }}
                className="text-orange-600 font-semibold hover:text-orange-700 hover:underline"
              >
                Registra ora
              </button>
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyLoginModal;
